import { isDefined } from "@clipboard-health/util-ts";
import { differenceInMinutes } from "date-fns";

const MINUTES_PER_HOUR = 60;

export interface ChangedCharge {
  initialRateInMinorUnits: number;
  currentRateInMinorUnits: number;
}

/** A per-qualification charge normalized for spend-limit projection, shared across charge-editing surfaces. */
export interface SpendLimitCharge extends ChangedCharge {
  qualification: string;
  fillGuaranteeThresholdInMinorUnits?: number;
}

export interface VariableChargeSpendLimitState {
  exceeded: boolean;
  overageInMinorUnits: number;
}

/**
 * Billable shift hours, excluding unpaid breaks (the backend's `durationWithoutBreaksInHours` basis).
 * Prefer the canonical `time` if a caller has it; otherwise approximate from wall-clock minus the break,
 * since the day-shifts payload exposes only `breakConfiguration.breakDurationInMinutes`. (The sibling
 * `getShiftDurationInMinutes` for verified shifts takes a break window, not minutes — kept separate.)
 */
export function getShiftDurationHours(shift: {
  time?: number;
  start: string;
  end: string;
  breakDurationInMinutes?: number;
}): number {
  if (typeof shift.time === "number" && shift.time > 0) {
    return shift.time;
  }

  const grossMinutes = differenceInMinutes(new Date(shift.end), new Date(shift.start));
  const netMinutes = grossMinutes - (shift.breakDurationInMinutes ?? 0);
  return netMinutes > 0 ? netMinutes / MINUTES_PER_HOUR : 0;
}

/**
 * Worst-case weekly boost-spend delta in minor units. Boost is `(charge − base) × hours`; the base
 * cancels in the delta, leaving `(newRate − initialRate) × hours`. A multi-qualification shift is filled
 * by one worker, so it counts once at the largest per-qualification delta (not the sum); `shiftCount`
 * scales it for `applyToAll`. Rounded to whole cents — billable hours are fractional, so an unrounded
 * sub-cent delta would leave `exceeded` true while the overage rounds to "$0.00" in the card.
 */
export function getVariableChargeSpendDeltaInMinorUnits(params: {
  changedCharges: ChangedCharge[];
  durationHours: number;
  shiftCount: number;
}): number {
  const { changedCharges, durationHours, shiftCount } = params;

  if (changedCharges.length === 0) {
    return 0;
  }

  const maxPerShiftDelta = Math.max(
    ...changedCharges.map(
      (charge) => (charge.currentRateInMinorUnits - charge.initialRateInMinorUnits) * durationHours
    )
  );
  return Math.round(maxPerShiftDelta * shiftCount);
}

/**
 * Whether the edit would push the week over its variableCharge limit, and by how much. Advisory only.
 * Only a *raise* can exceed — lowering (or not raising) is always allowed, even when already over.
 */
export function getVariableChargeSpendLimitState(params: {
  changedCharges: ChangedCharge[];
  durationHours: number;
  shiftCount: number;
  currentUsageInMinorUnits: number;
  limitInMinorUnits: number | undefined;
}): VariableChargeSpendLimitState {
  const { limitInMinorUnits, currentUsageInMinorUnits, ...delta } = params;

  if (!isDefined(limitInMinorUnits) || delta.changedCharges.length === 0) {
    return { exceeded: false, overageInMinorUnits: 0 };
  }

  const spendDeltaInMinorUnits = getVariableChargeSpendDeltaInMinorUnits(delta);
  if (spendDeltaInMinorUnits <= 0) {
    return { exceeded: false, overageInMinorUnits: 0 };
  }

  const projected = currentUsageInMinorUnits + spendDeltaInMinorUnits;
  const overageInMinorUnits = Math.max(0, projected - limitInMinorUnits);
  return { exceeded: overageInMinorUnits > 0, overageInMinorUnits };
}

interface SpendLimitProjectionParams {
  charges: SpendLimitCharge[];
  durationHours: number;
  shiftCount: number;
  currentUsageInMinorUnits: number;
  limitInMinorUnits: number | undefined;
}

/** Projects the changed charges at their chosen rates (filters out unchanged ones, then delegates). */
export function getSpendLimitStateForCharges(
  params: SpendLimitProjectionParams
): VariableChargeSpendLimitState {
  const { charges, ...rest } = params;
  const changedCharges = charges.filter(
    (charge) => charge.currentRateInMinorUnits !== charge.initialRateInMinorUnits
  );
  return getVariableChargeSpendLimitState({ changedCharges, ...rest });
}

/**
 * Spend-limit state for `qualification`'s chosen rate alone (its own delta × shiftCount), as if the
 * shift filled at that qualification — so each qualification is flagged independently. Other
 * qualifications are ignored, never summed.
 */
export function getQualificationSpendLimitState(
  params: SpendLimitProjectionParams & { qualification: string }
): VariableChargeSpendLimitState {
  const { charges, qualification, ...rest } = params;
  return getSpendLimitStateForCharges({
    charges: charges.filter((charge) => charge.qualification === qualification),
    ...rest,
  });
}

/** Projects the edit with `qualification` snapped to its FG threshold; no-op if it has none. */
export function getFillGuaranteeThresholdSpendLimitState(
  params: SpendLimitProjectionParams & { qualification: string }
): VariableChargeSpendLimitState {
  const { charges, qualification, ...rest } = params;

  const threshold = charges.find(
    (charge) => charge.qualification === qualification
  )?.fillGuaranteeThresholdInMinorUnits;
  if (!isDefined(threshold)) {
    return { exceeded: false, overageInMinorUnits: 0 };
  }

  const projectedCharges = charges.map((charge) => ({
    ...charge,
    currentRateInMinorUnits:
      charge.qualification === qualification ? threshold : charge.currentRateInMinorUnits,
  }));
  return getSpendLimitStateForCharges({ charges: projectedCharges, ...rest });
}
